import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  AccountViewListingStatus,
  OptimizationMode,
  PlacementSource,
  RentalManagerUserPropertySpotlightWebApiHandler_list_Path,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertySpotlightWebApiHandler_getOptimizationConfig,
  useRentalManagerUserPropertySpotlightWebApiHandler_list,
  useRentalManagerUserPropertySpotlightWebApiHandler_setOptimizationMode,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useSpotlightOverview } from '../../../hooks/queries/properties/zram';
import { useSpotlightMutations } from './hooks/useSpotlightMutations';
import { dropFrom } from './utils/cacheHelpers';

import { SpotlightReassignModal } from './SpotlightReassignModal';
import { SpotlightSwapModal } from './SpotlightSwapModal';

import {
  computeRabDeployment,
  type DeploymentResult,
  type OptimizableListing,
  type OptimizerId,
  RAB_LICENSES,
} from './rabDeployment';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

import type { ReactNode } from 'react';
import type { AccountViewListingDto } from '@zg-rentals/ts-rental-manager-api-client';

type Optimizable = OptimizableListing & { status: AccountViewListingStatus };

const FOR_RENT = new Set<AccountViewListingStatus>([
  AccountViewListingStatus.PUBLISHED,
  AccountViewListingStatus.PARTIALLY_PUBLISHED,
  AccountViewListingStatus.PUBLISHING,
  AccountViewListingStatus.EXPIRES_SOON,
  AccountViewListingStatus.LEASED,
]);
const DAY_MS = 86_400_000;
const EMPTY_IDS: Set<string> = new Set();

export type SpotlightOccupant = {
  alias: string;
  label: string;
  meta: string;
  origin: 'auto' | 'pinned';
};

export type AutoSpotlightContextValue = {
  active: boolean;
  autoIds: Set<string>;
  cancelSwap: () => void;
  confirmSwap: (targetAlias: string) => void;
  deployment: DeploymentResult<Optimizable>;
  exclude: (id: string) => void;
  excludedIds: Set<string>;
  /** Feed-managed (AppFolio/custom-feed) listings: read-only "manage in feed", never ZRM-controlled. */
  feedManagedIds: Set<string>;
  licenses: number;
  onChangeOptimizer: (id: OptimizerId) => void;
  optimizer: OptimizerId;
  /** Count of listings whose advertising placementSource is PINNED. */
  pinnedCount: number;
  /** Count of listings whose advertising placementSource is AUTO. */
  autoCount: number;
  /** Count of available spotlight placements. */
  availableCount: number;
  /** Per-listing package name (the plan the listing already pays for). Spotlight sits on top of it. */
  packageNameById: Record<string, string>;
  pinnedIds: Set<string>;
  pin: (id: string) => void;
  reassignCandidate: string | null;
  reassignCandidateLabel: string;
  reassignOccupants: Array<SpotlightOccupant>;
  cancelReassign: () => void;
  confirmReassign: (targetAlias: string) => void;
  setReassignCandidate: (id: string) => void;
  swapCandidate: string | null;
  swapCandidateLabel: string;
  swapOccupants: Array<SpotlightOccupant>;
  unexclude: (id: string) => void;
  unpin: (id: string) => void;
  vacantIds: Set<string>;
};

const AutoSpotlightContext = createContext<AutoSpotlightContextValue | null>(null);

export const useAutoSpotlight = () => useContext(AutoSpotlightContext);

export function AutoSpotlightProvider({
  active,
  children,
  initialExcludedIds = [],
  initialOptimizer = 'dom',
  initialPinnedIds = [],
  listings,
}: {
  active: boolean;
  children: ReactNode;
  initialExcludedIds?: Array<string>;
  initialOptimizer?: OptimizerId;
  initialPinnedIds?: Array<string>;
  listings: Array<AccountViewListingDto>;
}) {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const [bumped, setBumped] = useState<Set<string>>(() => new Set());
  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set(initialExcludedIds));
  const [userOverride, setUserOverride] = useState<OptimizerId | null>(null);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => new Set(initialPinnedIds));
  const [swapCandidate, setSwapCandidate] = useState<string | null>(null);
  const [reassignCandidate, setReassignCandidate] = useState<string | null>(null);

  // Single source of truth for spotlight counts (initialized from server, managed optimistically)
  const [spotlightCounts, setSpotlightCounts] = useState<{
    pinnedCount: number;
    autoCount: number;
    availableCount: number;
  } | null>(null);

  const { data: configData } = useRentalManagerUserPropertySpotlightWebApiHandler_getOptimizationConfig();
  const { mutateAsync: saveOptimizationMode } = useRentalManagerUserPropertySpotlightWebApiHandler_setOptimizationMode({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'setOptimizationMode', details: 'spotlight optimizer mode' },
    },
  });

  const configOptimizer: OptimizerId = configData?.optimizationMode === OptimizationMode.RENT ? 'rent' : 'dom';
  const optimizer = userOverride ?? (configData ? configOptimizer : initialOptimizer);

  // Real, account-wide Pinned / Auto-deployed totals from the spotlight overview endpoint (not paginated).
  const { data: overviewData } = useSpotlightOverview();

  // Fetch all spotlighted listings (for reassign modal)
  // Only fetch when reassign modal is open to avoid unnecessary API calls
  const { data: spotlightedListings } = useRentalManagerUserPropertySpotlightWebApiHandler_list({
    enabled: Boolean(reassignCandidate),
  });

  // Every fresh overview response is the source of truth — mutations invalidate it and the backend runs
  // the engine synchronously, so the refetched counts reflect the real post-re-rank state. The optimistic
  // deltas only bridge the invalidate→refetch window.
  useEffect(() => {
    if (overviewData) {
      setSpotlightCounts({
        pinnedCount: overviewData.pinnedCount ?? 0,
        autoCount: overviewData.autoCount ?? 0,
        availableCount: overviewData.availableCount ?? 0,
      });
    }
  }, [overviewData]);

  const labelByAlias = useMemo(() => {
    const map: Record<string, { label: string; meta: string }> = {};
    listings.forEach((listing) => {
      if (!listing) return;
      const city = listing.address?.city ?? '';
      const state = listing.address?.state ?? '';
      map[listing.listingAlias] = {
        label: listing.name || listing.listingAlias,
        meta: [city, state].filter(Boolean).join(', '),
      };
    });
    return map;
  }, [listings]);

  // Package name is separate from Spotlight: a listing keeps its plan whether or not it's spotlighted.
  // Today this comes from the fixture-injected field; RECE-1569 swaps in the real advertising data.
  const packageNameById = useMemo(() => {
    const map: Record<string, string> = {};
    listings.forEach((listing) => {
      if (!listing) return;
      const packageName = (listing as AccountViewListingDto & { packageName?: string }).packageName;
      if (packageName) {
        map[listing.listingAlias] = packageName;
      }
    });
    return map;
  }, [listings]);

  // Feed-managed (AppFolio / custom-feed) listings are read-only "manage in feed": the engine never
  // optimizes them and ZRM never pins/excludes them (RECE-1457 + 06-11 V1 scope lock). Fixture-injected
  // today; RECE-1569 sources this from the placement-detail gate (manageInFeed || appfolio || feedId-in-list).
  const feedManagedIds = useMemo(() => {
    const ids = new Set<string>();
    listings.forEach((listing) => {
      if (!listing) return;
      if ((listing as AccountViewListingDto & { manageInFeed?: boolean }).manageInFeed) {
        ids.add(listing.listingAlias);
      }
    });
    return ids;
  }, [listings]);

  const optimizable = useMemo<Array<Optimizable>>(() => {
    const now = Date.now();
    return listings
      .filter((listing): listing is AccountViewListingDto => Boolean(listing))
      .map((listing, index) => ({
        daysOnMarket: Math.max(1, Math.round((now - (listing.updatedMs ?? now)) / DAY_MS)),
        id: listing.listingAlias,
        market: [listing.address?.city, listing.address?.state].filter(Boolean).join(', ') || 'Unknown market',
        rent: (listing as AccountViewListingDto & { rent?: number }).rent ?? 2000 + index * 100,
        status: listing.status?.status ?? AccountViewListingStatus.OFF_MARKET,
      }));
  }, [listings]);

  const isVacant = (listing: Optimizable) => FOR_RENT.has(listing.status);
  const vacantIds = useMemo(() => new Set(optimizable.filter(isVacant).map((listing) => listing.id)), [optimizable]);

  // The engine optimizes globally across all eligible listings, so license accounting stays portfolio-wide.
  const deployment = computeRabDeployment({
    excludeIds: new Set([...bumped, ...excludedIds, ...feedManagedIds]),
    isVacant,
    listings: optimizable,
    optimizer,
    pinnedIds,
  });

  const labelFor = (id: string) => labelByAlias[id]?.label ?? id;
  const packageFor = (id: string) => packageNameById[id] ?? COPY.packageNameFallback;

  // Initialize mutation helpers
  const mutations = useSpotlightMutations({
    labelFor,
    packageFor,
    spotlightCounts,
    setSpotlightCounts,
  });

  const doPin = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'pin',
      mutationFn: () => mutations.pinMutation({ getParams: { listingAlias: id } }),
      targetSource: PlacementSource.PINNED,
      successMessage: `${labelFor(id)} is pinned — it’ll always stay in Spotlight.`,
      errorMessage: `Failed to pin ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
        excludedIds: new Set(excludedIds),
        bumped: new Set(bumped),
      }),
      applyOptimisticUpdate: () => {
        setBumped((prev) => dropFrom(prev, id));
        setExcludedIds((prev) => dropFrom(prev, id));
        setPinnedIds((prev) => new Set(prev).add(id));
      },
      restoreSnapshot: (snapshot) => {
        setBumped(snapshot.bumped);
        setExcludedIds(snapshot.excludedIds);
        setPinnedIds(snapshot.pinnedIds);
      },
    });
  };

  const pinWithAutoBump = async (id: string, victimId: string) => {
    await mutations.executeMutation({
      id,
      action: 'pin',
      mutationFn: () => mutations.pinMutation({ getParams: { listingAlias: id } }),
      targetSource: PlacementSource.PINNED,
      successMessage: `${labelFor(id)} pinned to Spotlight. ${labelFor(victimId)} removed from Spotlight.`,
      errorMessage: `Failed to pin ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
        excludedIds: new Set(excludedIds),
        bumped: new Set(bumped),
      }),
      applyOptimisticUpdate: () => {
        setBumped((prev) => new Set(prev).add(victimId));
        setExcludedIds((prev) => dropFrom(prev, id));
        setPinnedIds((prev) => new Set(prev).add(id));
      },
      restoreSnapshot: (snapshot) => {
        setBumped(snapshot.bumped);
        setExcludedIds(snapshot.excludedIds);
        setPinnedIds(snapshot.pinnedIds);
      },
    });
  };

  const pin = (id: string) => {
    if (pinnedIds.has(id)) {
      return;
    }
    if (deployment.autoIds.has(id) || deployment.idle > 0) {
      doPin(id);
      return;
    }
    const autoVictim = deployment.ranked.filter((listing) => deployment.autoIds.has(listing.id)).at(-1);
    if (autoVictim) {
      pinWithAutoBump(id, autoVictim.id);
      return;
    }
    setSwapCandidate(id);
  };

  // Any override mutation re-runs the engine in prod, which rebalances every license. The mock mimics that
  // by clearing the one-off auto-bump tracking so a freed/changed license re-ranks instead of staying sticky.
  const unpin = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'unpin',
      mutationFn: () => mutations.unpinMutation({ getParams: { listingAlias: id } }),
      targetSource: PlacementSource.AUTO,
      successMessage: `${labelFor(id)} returned to auto-optimize.`,
      errorMessage: `Failed to unpin ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
        bumped: new Set(bumped),
      }),
      applyOptimisticUpdate: () => {
        setBumped(new Set());
        setPinnedIds((prev) => dropFrom(prev, id));
      },
      restoreSnapshot: (snapshot) => {
        setBumped(snapshot.bumped);
        setPinnedIds(snapshot.pinnedIds);
      },
    });
  };

  const exclude = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'exclude',
      mutationFn: () => mutations.excludeMutation({ getParams: { listingAlias: id } }),
      targetSource: PlacementSource.EXCLUDED,
      successMessage: `${labelFor(id)} removed from Spotlight — ${packageFor(id)} only.`,
      successTone: 'info',
      errorMessage: `Failed to exclude ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
        excludedIds: new Set(excludedIds),
        bumped: new Set(bumped),
      }),
      applyOptimisticUpdate: () => {
        setBumped(new Set());
        setPinnedIds((prev) => dropFrom(prev, id));
        setExcludedIds((prev) => new Set(prev).add(id));
      },
      restoreSnapshot: (snapshot) => {
        setBumped(snapshot.bumped);
        setPinnedIds(snapshot.pinnedIds);
        setExcludedIds(snapshot.excludedIds);
      },
    });
  };

  const unexclude = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'unexclude',
      mutationFn: () => mutations.unexcludeMutation({ getParams: { listingAlias: id } }),
      targetSource: PlacementSource.AUTO,
      successMessage: `${labelFor(id)} is back in auto-optimize.`,
      errorMessage: `Failed to include ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        excludedIds: new Set(excludedIds),
        bumped: new Set(bumped),
      }),
      applyOptimisticUpdate: () => {
        setBumped(new Set());
        setExcludedIds((prev) => dropFrom(prev, id));
      },
      restoreSnapshot: (snapshot) => {
        setBumped(snapshot.bumped);
        setExcludedIds(snapshot.excludedIds);
      },
    });
  };

  const confirmSwap = async (targetAlias: string) => {
    if (!swapCandidate) {
      return;
    }
    const incoming = swapCandidate;

    // Capture snapshot before changes
    const previousPinned = new Set(pinnedIds);
    const previousExcluded = new Set(excludedIds);
    const previousSwapCandidate = swapCandidate;

    // Optimistic update
    setPinnedIds((prev) => {
      const next = new Set(prev);
      next.delete(targetAlias);
      next.add(incoming);
      return next;
    });
    setExcludedIds((prev) => dropFrom(prev, incoming));
    mutations.updateAdvertisingCache(incoming, PlacementSource.PINNED);
    setSwapCandidate(null);

    try {
      await mutations.pinMutation({ getParams: { listingAlias: incoming } });
      // Success - keep optimistic update (no count change for swap)
      enqueueToast(
        <Toast id="spotlight-swapped" tone="success">
          {labelFor(incoming)} pinned to Spotlight. {labelFor(targetAlias)} moved to {packageFor(targetAlias)}.
        </Toast>,
      );
    } catch (error) {
      // Rollback
      setPinnedIds(previousPinned);
      setExcludedIds(previousExcluded);
      mutations.updateAdvertisingCache(incoming, previousExcluded.has(incoming) ? PlacementSource.EXCLUDED : null);
      setSwapCandidate(previousSwapCandidate);
      enqueueToast(
        <Toast id="spotlight-swap-error" tone="critical">
          Failed to swap Spotlight. Try again.
        </Toast>,
      );
      logError(error);
    }
  };

  const confirmReassign = async (targetAlias: string) => {
    if (!reassignCandidate) {
      return;
    }
    const incoming = reassignCandidate;

    // Capture snapshot before changes
    const previousPinned = new Set(pinnedIds);
    const previousExcluded = new Set(excludedIds);
    const previousReassignCandidate = reassignCandidate;

    // Optimistic update: swap pinned listings
    setPinnedIds((prev) => {
      const next = new Set(prev);
      next.delete(targetAlias);
      next.add(incoming);
      return next;
    });
    setExcludedIds((prev) => dropFrom(prev, incoming));
    mutations.updateAdvertisingCache(incoming, PlacementSource.PINNED);
    mutations.updateAdvertisingCache(targetAlias, PlacementSource.AUTO);
    setReassignCandidate(null);

    try {
      await mutations.reassignMutation({
        getParams: { fromListingAlias: targetAlias, toListingAlias: incoming },
      });
      // Success - keep optimistic update (no count change for reassign)
      // Invalidate the spotlight list cache so the modal shows updated data next time
      queryClient.invalidateQueries({ queryKey: [RentalManagerUserPropertySpotlightWebApiHandler_list_Path] });
      enqueueToast(
        <Toast id="spotlight-reassigned" tone="success">
          {labelFor(incoming)} pinned to Spotlight. {labelFor(targetAlias)} moved to {packageFor(targetAlias)}.
        </Toast>,
      );
    } catch (error) {
      // Rollback
      setPinnedIds(previousPinned);
      setExcludedIds(previousExcluded);
      mutations.updateAdvertisingCache(incoming, previousExcluded.has(incoming) ? PlacementSource.EXCLUDED : null);
      mutations.updateAdvertisingCache(targetAlias, PlacementSource.PINNED);
      setReassignCandidate(previousReassignCandidate);
      enqueueToast(
        <Toast id="spotlight-reassign-error" tone="critical">
          Failed to reassign Spotlight. Try again.
        </Toast>,
      );
      logError(error);
    }
  };

  const swapOccupants: Array<SpotlightOccupant> = optimizable
    .filter((listing) => isVacant(listing) && (pinnedIds.has(listing.id) || deployment.autoIds.has(listing.id)))
    .map((listing) => ({
      alias: listing.id,
      label: labelByAlias[listing.id]?.label ?? listing.id,
      meta: labelByAlias[listing.id]?.meta ?? '',
      origin: pinnedIds.has(listing.id) ? 'pinned' : 'auto',
    }));

  // For reassign modal, show all pinned properties as replacement options
  // Use the spotlight list API which returns all spotlighted listings
  const reassignOccupants: Array<SpotlightOccupant> = useMemo(() => {
    // Only compute when modal is actually open and data is loaded
    if (!reassignCandidate || !spotlightedListings?.listings) {
      return [];
    }

    // Map the spotlighted listings to occupants format
    return spotlightedListings.listings.map((listing) => {
      const city = listing.address?.city ?? '';
      const state = listing.address?.state ?? '';
      return {
        alias: listing.listingAlias,
        label: listing.name || listing.listingAlias,
        meta: [city, state].filter(Boolean).join(', '),
        origin: 'pinned' as const,
      };
    });
  }, [reassignCandidate, spotlightedListings]);

  // Use spotlightCounts as single source of truth (fallback to server data if not initialized)
  const pinnedCount = spotlightCounts?.pinnedCount ?? overviewData?.pinnedCount ?? 0;
  const autoCount = spotlightCounts?.autoCount ?? overviewData?.autoCount ?? 0;
  const availableCount = spotlightCounts?.availableCount ?? overviewData?.availableCount ?? 0;

  // Calculate total licenses from overview data (pinnedCount + autoCount + availableCount)
  // Fallback to RAB_LICENSES constant if no overview data is available
  const totalLicenses = overviewData ? pinnedCount + autoCount + availableCount : RAB_LICENSES;

  const value: AutoSpotlightContextValue = {
    active,
    autoIds: active ? deployment.autoIds : EMPTY_IDS,
    cancelSwap: () => setSwapCandidate(null),
    confirmSwap,
    deployment,
    exclude,
    excludedIds,
    feedManagedIds,
    licenses: totalLicenses,
    pinnedCount,
    autoCount,
    availableCount,
    onChangeOptimizer: (id) => {
      const previousOverride = userOverride;
      const previousBumped = bumped;
      setUserOverride(id);
      setBumped(new Set());
      void saveOptimizationMode({
        postData: {
          optimizationMode: id === 'rent' ? OptimizationMode.RENT : OptimizationMode.DOM,
        },
      })
        .then(() => {
          enqueueToast(
            <Toast id="spotlight-optimizer-saved" tone="success">
              Optimize for updated to {id === 'rent' ? 'Rent' : 'Days on market'}.
            </Toast>,
          );
          return;
        })
        .catch(() => {
          // Snap the control back to the last persisted mode — the server rejected this change.
          setUserOverride(previousOverride);
          setBumped(previousBumped);
          enqueueToast(
            <Toast id="spotlight-optimizer-error" tone="critical">
              Optimize for setting could not be saved. Try again.
            </Toast>,
          );
          return;
        });
    },
    optimizer,
    packageNameById,
    pin,
    pinnedIds,
    reassignCandidate,
    reassignCandidateLabel: reassignCandidate ? labelFor(reassignCandidate) : '',
    reassignOccupants,
    cancelReassign: () => setReassignCandidate(null),
    confirmReassign,
    setReassignCandidate,
    swapCandidate,
    swapCandidateLabel: swapCandidate ? labelFor(swapCandidate) : '',
    swapOccupants,
    unexclude,
    unpin,
    vacantIds,
  };

  return (
    <AutoSpotlightContext.Provider value={value}>
      {children}
      <SpotlightSwapModal />
      <SpotlightReassignModal />
    </AutoSpotlightContext.Provider>
  );
}
